import { useState, useEffect } from 'react';
import { Heart, BookOpen, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import BottomNav from '../components/BottomNav';

interface FavoriteCard {
  id: string;
  card_id: string;
  title: string;
  content: string;
  card_type: string;
  book_title: string;
  created_at: string;
}

export default function Favorites() {
  const [favorites, setFavorites] = useState<FavoriteCard[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    try {
      const response = await api.get('/favorites');
      setFavorites(response.data);
    } catch (error) {
      console.error('加载收藏失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (id: string) => {
    try {
      await api.delete(`/favorites/${id}`);
      setFavorites(favorites.filter(f => f.id !== id));
    } catch (error) {
      alert('取消收藏失败');
    }
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      theory: '#22a9d9',
      evidence: '#6c5ce7',
      method: '#00b894',
      story: '#fd79a8',
      insight: '#fdcb6e',
      summary: '#74b9ff'
    };
    return colors[type] || '#22a9d9';
  };

  const getTypeName = (type: string) => {
    const names: Record<string, string> = {
      theory: '理论',
      evidence: '证据',
      method: '方法',
      story: '故事',
      insight: '洞察',
      summary: '总结'
    };
    return names[type] || type;
  };

  return (
    <div className="min-h-screen bg-[#1a2e3e] pb-24">
      {/* 头部 */}
      <div className="p-6">
        <h1 className="text-3xl font-bold text-white mb-2">我的收藏</h1>
        <p className="text-white/60">共 {favorites.length} 张卡片</p>
      </div>

      {/* 收藏列表 */}
      <div className="px-6 space-y-4">
        {loading ? (
          <div className="text-center py-12 text-white/60">
            加载中...
          </div>
        ) : favorites.length === 0 ? (
          <div className="text-center py-12">
            <Heart className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <p className="text-white/60">还没有收藏任何卡片</p>
          </div>
        ) : (
          favorites.map((favorite) => (
            <div key={favorite.id} className="glass rounded-2xl p-5">
              {/* 头部 */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: `${getTypeColor(favorite.card_type)}20`,
                      color: getTypeColor(favorite.card_type)
                    }}
                  >
                    {getTypeName(favorite.card_type)}
                  </div>
                  <span className="text-white/40 text-sm">·</span>
                  <span className="text-white/60 text-sm">{favorite.book_title}</span>
                </div>
                <button
                  onClick={() => handleRemove(favorite.id)}
                  className="text-red-400 hover:text-red-300 transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>

              {/* 标题 */}
              <h3 className="text-white font-medium text-lg mb-3">
                {favorite.title}
              </h3>

              {/* 内容 */}
              <p className="text-white/70 text-sm leading-relaxed line-clamp-3">
                {favorite.content}
              </p>

              {/* 时间 */}
              <div className="mt-4 text-white/40 text-xs">
                收藏于 {new Date(favorite.created_at).toLocaleDateString()}
              </div>
            </div>
          ))
        )}
      </div>

      <BottomNav />
    </div>
  );
}
